import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Sidebar from "./Sidebar";
import MainContainer from "./MainContainer";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { DialogCloseIcon } from "../../../../assets";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  DISTANCE_UNIT,
  EMPTY_STRING,
  featuresTabs,
  MILES_TO_KM,
  ZERO_VALUE,
} from "../../../../constants";
import { featuresModalSchema } from "../../../../schemas";
import { formatToTwoDecimalPlaces } from "../../../../shared/utility";

const normalizeNearbyLocations = (locations = []) =>
  locations.map((location) => {
    const miles = Number(location.distance);
    const hasDistanceKm =
      location.distanceKm !== undefined &&
      location.distanceKm !== null &&
      location.distanceKm !== EMPTY_STRING;
    const distanceKm = hasDistanceKm
      ? location.distanceKm
      : Number.isFinite(miles) && miles >= ZERO_VALUE
      ? Number(formatToTwoDecimalPlaces(miles * MILES_TO_KM))
      : ZERO_VALUE;

    return {
      ...location,
      distance: Number.isFinite(miles) ? miles : ZERO_VALUE,
      distanceKm,
      distanceUnit: location.distanceUnit ?? DISTANCE_UNIT.MILES,
    };
  });

const FeaturesModal = ({ show, onClose }) => {
  const [erroredFeatures, setErroredFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(1);
  const { setValue, getValues } = useFormContext();
  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(featuresModalSchema),
  });
  const {
    formState: { errors },
  } = methods;

  const changeSelectedFeature = (id) => {
    setSelectedFeature(id);
  };

  const handleSubmit = (data) => {
    setValue("nearbyLocations", data.amenity.nearbyLocations ?? []);
    const { nearbyLocations: _ignored, ...amenityWithoutLocations } =
      data.amenity;
    setValue("amenity", {
      ...(getValues("amenity") ?? {}),
      ...amenityWithoutLocations,
    });

    onClose();
  };

  useEffect(() => {
    if (show) {
      methods.reset({
        amenity: {
          ...getValues("amenity"),
          nearbyLocations: normalizeNearbyLocations(
            getValues("nearbyLocations") ?? []
          ),
        },
      });
    } else {
      setSelectedFeature(1);
    }
    // eslint-disable-next-line
  }, [show]);

  useEffect(() => {
    const errorKeys = Object.keys(errors?.amenity || {});

    const featureGroups = [
      {
        keys: [
          "builtInYear",
          "electricityBackup",
          "parkingSpace",
          "propertyView",
          "flooringType",
          "featureIds",
        ],
        featureId: 1,
      },
      {
        keys: [
          "otherRooms",
          "staffQuarters",
          "kitchens",
          "storeRooms",
          "floors",
          "roomsIds",
        ],
        featureId: 2,
      },
      {
        keys: ["facilitesIds"],
        featureId: 3,
      },
      {
        keys: ["nearbyLocations"],
        featureId: 4,
      },
    ];

    const newFeatures = featureGroups.reduce((acc, { keys, featureId }) => {
      if (keys.some((key) => errorKeys.includes(key))) {
        acc.push(featureId);
      }
      return acc;
    }, []);

    setErroredFeatures(newFeatures);

    // eslint-disable-next-line
  }, [errors]);

  return (
    <Modal show={show} dialogClassName="features-modal position-relative">
      <Modal.Body className="d-block d-md-flex">
        <img
          src={DialogCloseIcon}
          className="position-absolute modal-close-icon pointer"
          onClick={onClose}
          alt={EMPTY_STRING}
        />
        <Sidebar
          features={featuresTabs}
          selected={selectedFeature}
          changeSelectedFeature={changeSelectedFeature}
          erroredFeatures={erroredFeatures}
        />
        <FormProvider {...methods}>
          <MainContainer
            selectedFeature={selectedFeature}
            onSubmit={handleSubmit}
          />
        </FormProvider>
      </Modal.Body>
    </Modal>
  );
};

export default FeaturesModal;
